var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useCallback, useState, } from "react";
import { apolloSpaceInlineHalfX } from "@nexthink/apollo-tokens";
import { StyledTableHeaderCell, StyledKebabButton, StyledTableHeaderContent, StyledHeaderContentWrapper, StyledHeaderWithTags, StyledMiniTagList, StyledSingleLineEllipsis, } from "./TableHeaderCell.style";
import Icon from "../icons/Icon";
import Box from "../box/Box";
import { StyledKebabPlaceholder } from "./KebabWithPortal.style";
import { TableKebabActionMenu, renderTableActionAsActionItems } from "./KebabWithPortal";
import { hasTags } from "./TableColumn";
import { cellActions } from "./utils";
import { ThemeContext } from "../../theme/ThemeProvider";
import { isTableFillerType } from "./TableFillerColumn";
import { withContextMenu } from "../menu/ContextActionMenu";
import { ResponsiveLinesEllipsis } from "../value-list/ValueListItem";
import { WithTooltip } from "../with-tooltip";
import useRuler from "../../hooks/useRuler";
import { StyledTH } from "./Table.style";
const SortIcon = ({ sort }) => {
    const theme = useContext(ThemeContext);
    return (React.createElement(Box, { as: "span", ml: apolloSpaceInlineHalfX },
        React.createElement(Icon, { name: sort === "asc" ? "sortAmountUp" : "sortAmountDown", style: {
                color: theme.colors.apolloColorTextSecondary,
                width: "1rem",
                height: "1rem",
                verticalAlign: "top",
            } })));
};
const sortSeq = {
    asc: "desc",
    desc: "asc",
    "": "asc",
};
const ariaSortMap = {
    none: "none",
    asc: "ascending",
    desc: "descending",
};
const showSort = (sortable, sort, field) => {
    return sortable && sort && sort.field === field;
};
const useTableHeaderCell = ({ column, columnIndex, sort, onSort, }) => {
    const header = column.title || column.field;
    // mostly copied from kendo's HeaderRow, used the same approach to keep the same API
    const setSort = (e, previousSort) => {
        if (!onSort) {
            return;
        }
        const dir = sortSeq[previousSort && previousSort.dir && previousSort.field === column.field ? previousSort.dir : ""];
        const newDescriptor = [];
        if (column.field) {
            newDescriptor.push({ field: column.field, dir });
        }
        onSort({ sort: newDescriptor, syntheticEvent: e, nativeEvent: e.nativeEvent });
    };
    const onClick = (e) => {
        e.preventDefault();
        column.sortable && setSort(e, sort);
    };
    const onKeyDown = (e) => {
        e.key === "Enter" && onClick(e);
    };
    return {
        tableHeaderProps: Object.assign(Object.assign(Object.assign({ locked: column.locked }, ((sort === null || sort === void 0 ? void 0 : sort.field) === column.field && { "aria-sort": ariaSortMap[(sort === null || sort === void 0 ? void 0 : sort.dir) || "none"] })), { "aria-selected": "false", colSpan: 1, rowSpan: 1 }), (isTableFillerType(column.type)
            ? {
                "aria-hidden": true,
            }
            : Object.assign(Object.assign({ role: "columnheader" }, (columnIndex !== undefined && { "aria-colindex": columnIndex + 1 })), { "aria-rowindex": 1 }))),
        columnTitle: column.tooltip === "" ? undefined : column.tooltip || header,
        header,
        onClick,
        onKeyDown,
    };
};
export const SingleLineEllipsis = ({ text, onReflow }) => {
    const { ref } = useRuler(onReflow);
    return (React.createElement(StyledSingleLineEllipsis, { className: "SingleLineEllipsis", ref: ref }, text));
};
// eslint-disable-next-line sonarjs/cognitive-complexity
const TableHeaderCell = (props) => {
    const { id, "data-insights": insightsAttr, field, column, sort, tableLayout } = props;
    const { actionMenuRenderer, sortable, headerCellRenderer, align, type, columnHeaderMenuRenderer, headerTooltip } = column;
    const { tableHeaderProps, columnTitle, header, onClick, onKeyDown } = useTableHeaderCell(props);
    const withTags = hasTags(column);
    const showSortIcon = showSort(sortable, sort, field);
    const [isContentEllipsed, setIsContentEllipsed] = useState(false);
    const tryToFixOverflow = () => {
        const valueItemEl = document.getElementById(id);
        const headerName = valueItemEl === null || valueItemEl === void 0 ? void 0 : valueItemEl.querySelector(withTags ? ".SingleLineEllipsis" : ".LinesEllipsis");
        if (withTags) {
            if (headerName && headerName.offsetWidth < headerName.scrollWidth) {
                setIsContentEllipsed(true);
            }
        }
        else {
            if (headerName && headerName.offsetHeight < headerName.scrollHeight) {
                setIsContentEllipsed(true);
            }
        }
    };
    const renderHeaderName = () => {
        if (headerCellRenderer) {
            return headerCellRenderer(props);
        }
        return withTags ? (React.createElement(SingleLineEllipsis, { text: header, onReflow: () => {
                tryToFixOverflow();
            } })) : (React.createElement(ResponsiveLinesEllipsis, { text: header, maxLine: 2, onReflow: ({ clamped }) => {
                setIsContentEllipsed(clamped);
                tryToFixOverflow();
            }, basedOn: "letters" }));
    };
    const renderContent = () => {
        const headerContent = (React.createElement(StyledTableHeaderContent, { column: column, tabIndex: sortable || headerTooltip || isContentEllipsed ? 0 : -1, id: `${id}_header`, sortable: Boolean(sortable), tableLayout: tableLayout, withTags: withTags }, renderHeaderName()));
        return (React.createElement(StyledHeaderContentWrapper, { column: column },
            headerTooltip || isContentEllipsed ? (React.createElement(WithTooltip, Object.assign({ alignment: "bottomLeft" }, (headerTooltip ? headerTooltip : { tooltipContents: header })), headerContent)) : (headerContent),
            showSortIcon && React.createElement(SortIcon, { sort: sort === null || sort === void 0 ? void 0 : sort.dir })));
    };
    const renderWithTags = () => (React.createElement(StyledHeaderWithTags, null,
        renderContent(),
        React.createElement(StyledMiniTagList, { tags: column.tags, column: column })));
    const content = withTags ? renderWithTags() : renderContent();
    // remove dataIndex and dataItem from kebab params
    const kebabParams = Object.assign(Object.assign({}, props), { dataItem: undefined, dataIndex: undefined, id: props.id || "", originalContent: content });
    const contextMenuRenderer = () => {
        return Promise.resolve(columnHeaderMenuRenderer === null || columnHeaderMenuRenderer === void 0 ? void 0 : columnHeaderMenuRenderer(kebabParams)).then((contextCellActions) => {
            const isCellActions = Array.isArray(contextCellActions) && contextCellActions.length > 0;
            const contextMenuActions = [...(isCellActions ? cellActions(contextCellActions) : [])];
            return renderTableActionAsActionItems(contextMenuActions, kebabParams, insightsAttr && `${insightsAttr}_context_menu`);
        });
    };
    const headerCell = (React.createElement(StyledTableHeaderCell, { id: id, "data-insights": insightsAttr, textAlign: align, column: column, withTags: withTags, "$type": type, onClick, onKeyDown, sort, columnTitle }, columnHeaderMenuRenderer ? (React.createElement(React.Fragment, null,
        actionMenuRenderer && align === "center" && React.createElement(StyledKebabPlaceholder, null),
        content,
        React.createElement(StyledKebabButton, null,
            React.createElement(TableKebabActionMenu, { id: `kebab-${id}`, "data-insights": insightsAttr && `kebab-${insightsAttr}`, actions: columnHeaderMenuRenderer, params: kebabParams, "aria-label": `Actions for column ${header}` })))) : (React.createElement(React.Fragment, null,
        content,
        actionMenuRenderer && align === "right" && React.createElement(StyledKebabPlaceholder, null)))));
    const TableHeaderWithActionsTarget = useCallback(function TableCellWithActions(_a) {
        var { 
        // unused props
        hide, isVisible, tooltipPosition, show, update, actions, handleQuickFind, tabIndex, onKeyDown, forwardedRef } = _a, contextMenuProps = __rest(_a, ["hide", "isVisible", "tooltipPosition", "show", "update", "actions", "handleQuickFind", "tabIndex", "onKeyDown", "forwardedRef"]);
        return (React.createElement(StyledTH, Object.assign({ ref: forwardedRef }, tableHeaderProps, contextMenuProps), headerCell));
    }, [headerCell, tableHeaderProps]);
    const TableCellWithActions = withContextMenu(TableHeaderWithActionsTarget);
    return columnHeaderMenuRenderer ? (React.createElement(TableCellWithActions, { actions: contextMenuRenderer })) : (React.createElement(StyledTH, Object.assign({}, tableHeaderProps), headerCell));
};
export default TableHeaderCell;
//# sourceMappingURL=TableHeaderCell.js.map